/**
 * Der Bearbeitungsstand EINES Papiers aus „Vollmachten u. Verfügungen"
 * (`type: "dokumentStatus"`).
 *
 * Drei Schaltflächen statt eines Hakens — „liegt vor", „wird eingeholt",
 * „nicht erforderlich". Der Grund steht bei {@link DOKUMENT_STATUS_WERTE}:
 * Ein leerer Haken vermengt „fehlt noch" mit „fällt bei diesem Kunden nicht
 * an", und genau diese Unterscheidung ist die Nachfassliste des Beraters.
 *
 * **Kein Pflichtfeld, auch nicht bei `pflichtpapier`.** Die Erstinformation
 * ist gewerberechtlich vorgeschrieben, aber die Analyse ist nicht der Ort, an
 * dem das durchgesetzt wird: Wer sie erst nach dem Termin unterschrieben
 * zurückbekommt, muss seine Analyse trotzdem speichern können. Die Markierung
 * ist Hervorhebung, keine Sperre.
 *
 * **Das Datum überlebt den Statuswechsel.** Es gehört fachlich zu „liegt vor"
 * und wird nur dort angezeigt — gelöscht wird es beim Umschalten aber nicht:
 * Ein Fehlklick auf „wird eingeholt" soll nicht zusätzlich das eingetippte
 * Unterschriftsdatum kosten.
 */
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import * as React from "react";
import {
  DOKUMENT_STATUS_WERTE,
  dokumentStatusWert,
  type DokumentStatusCode,
  type DokumentStatusWert,
  type FieldDef,
} from "@kmvs/forms-core";
import { BRAND, REFINED } from "./tokens";

const useStyles = makeStyles({
  wrap: {
    display: "grid",
    rowGap: "8px",
  },
  pflichtHinweis: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    alignSelf: "start",
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", REFINED.goldLine),
    backgroundColor: REFINED.goldBg,
    color: REFINED.goldInk,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
  },
  auswahl: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "6px",
  },
  knopf: {
    ...shorthands.padding("7px", "12px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.fieldBg },
  },
  knopfAktiv: {
    ...shorthands.border("1px", "solid", REFINED.aub),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub700,
    fontWeight: 600,
  },
  datumZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  datumLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
  },
  datumFeld: {
    ...shorthands.padding("7px", "10px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    backgroundColor: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.ink,
  },
});

type Props = {
  field: FieldDef;
  value: unknown;
  onChange: (wert: DokumentStatusWert) => void;
  /** Anzeige ohne Bedienung — Zusammenfassung, Prüf-Ansicht. */
  readonly?: boolean;
};

const DokumentStatusField = ({ field, value, onChange, readonly }: Props) => {
  const styles = useStyles();
  const stand = dokumentStatusWert(value);
  const datumId = React.useId();

  const setzeStatus = (status: DokumentStatusCode) => {
    if (readonly) return;
    // Nochmaliges Klicken derselben Schaltfläche nimmt die Angabe zurück —
    // sonst liesse sich ein versehentlich gesetzter Stand nie mehr auf
    // „nichts eingetragen" bringen.
    onChange(stand.status === status ? { ...stand, status: undefined } : { ...stand, status });
  };

  return (
    <div className={styles.wrap}>
      {field.pflichtpapier && (
        <span className={styles.pflichtHinweis}>Gesetzlich vorgeschrieben</span>
      )}
      <div className={styles.auswahl} role="group" aria-label={field.label ?? "Stand"}>
        {DOKUMENT_STATUS_WERTE.map((option) => {
          const aktiv = stand.status === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={aktiv}
              disabled={readonly}
              className={mergeClasses(styles.knopf, aktiv && styles.knopfAktiv)}
              onClick={() => setzeStatus(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {stand.status === "liegt_vor" && (
        <div className={styles.datumZeile}>
          <label className={styles.datumLabel} htmlFor={datumId}>
            Unterschrieben am
          </label>
          <input
            id={datumId}
            type="date"
            className={styles.datumFeld}
            value={stand.datum ?? ""}
            disabled={readonly}
            onChange={(e) =>
              onChange({ ...stand, datum: e.target.value || undefined })
            }
          />
        </div>
      )}
    </div>
  );
};

export default DokumentStatusField;
